import {
  CopyOutlined,
  DollarOutlined,
  QuestionCircleOutlined,
  UserOutlined,
} from '@ant-design/icons'
import {
  Avatar,
  Button,
  Form,
  GetProp,
  Input,
  message,
  Modal,
  Popover,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
  Upload,
  UploadProps,
} from 'antd'
import copy from 'copy-to-clipboard'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import UploadButton from 'component/button/upload-button'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { usePermission } from 'hooks/use-permission'
import { logError } from 'utils/logger'
import { isValidETHAddress } from 'utils/validate-fields'

import Countdown from './countdown'

import './style.scss'

const { Text } = Typography

interface DataType {
  render(
    text: any,
    record: DataType
  ): React.ReactNode | import('rc-table/lib/interface').RenderedCell<DataType>
  key: React.Key
  poolId: number
  earLogo?: any
  poolName: string
  poolContract: string
  stackingTime: string
  status: number
  stakingTimeSecond: number
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

const Staking = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [filterForm] = Form.useForm()

  const [dataStaking, setDataStaking]: any = useState([])
  const [dataViewInfo, setDataViewInfo]: any = useState()
  const [filter, setFilter] = useState<any>({})
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState<number>(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [ellipsis] = useState(true)
  const [loading, setLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage()
  const [poolContract, setPoolContract] = useState('')
  // const [filterStatus, setFilterStatus] = useState(null)
  const [, setShowContractDetail] = useState(false)
  const [, setGetContractDetail] = useState<undefined | any>(undefined)

  const { canCRUD } = usePermission()
  const canEditStaking = canCRUD(FEATURE.STAKING_POOL)

  // const handleFilterChange = ({ pagination, filters }: any) => {
  //   setFilterStatus(filters.status ? filters.status[0] : null) // Update filter state
  // }

  // const resetFilter = () => {
  //   setFilterStatus(null) // Reset filter state
  // }

  const showContract = (record: any) => {
    setGetContractDetail(record)
    setShowContractDetail(true)
  }

  useEffect(() => {
    getDataStaking({ ...pagination, ...filter, poolContract })
  }, [pagination, filter, poolContract])

  const getDataStaking = async (payload: any) => {
    try {
      const res = await AppAdmin.getListStaking(payload)
      if (res?.data.success === true) {
        setDataStaking(res?.data.data)
        setTotal(res?.data.totalCount)
      }
    } catch (error) {
      logError('Error: ', error)
    }
  }

  const showEditModal = async (record: any) => {
    setIsModalOpen(true)
    setModalMode('update')
    form.setFieldsValue(record)
  }

  const handleClickCreate = () => {
    form.resetFields() // Clear form fields
    setModalMode('create') // Set mode to create
    setIsModalOpen(true)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setDataViewInfo(null)
    form.resetFields()
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: 'Logo',
      dataIndex: 'earLogo',
      key: 'earLogo',
      render: (logo) => <Avatar src={logo} size={50} />,
    },
    {
      title: 'Pool Name',
      dataIndex: 'poolName',
      key: 'poolName',
      sorter: (a, b) => a.poolName.localeCompare(b.poolName),
      render: (text, record) => (
        <Button
          type="link"
          block
          color="primary"
          className="button-pool-name"
          onClick={() => {
            navigate(`/staking-detail/${record?.poolContract}`)
            showContract(record)
          }}
        >
          <Text
            style={ellipsis ? { width: 150 } : undefined}
            ellipsis={ellipsis ? { tooltip: text } : false}
            className="text-pool-name"
          >
            {text}
          </Text>
        </Button>
      ),
    },
    {
      title: 'Contract Address',
      dataIndex: 'poolContract',
      key: 'poolContract',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/address/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
              style={{ marginLeft: '8px' }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Status Time',
      dataIndex: 'stakingTimeSecond',
      key: 'stakingTimeSecond',
      sorter: (a, b) => a.stakingTimeSecond - b.stakingTimeSecond,
      render: (text: any, record: DataType) => (
        <Countdown stakingTimeSecond={record.stakingTimeSecond} className="status-success" />
      ),
    },
    {
      title: 'Show on Web',
      dataIndex: 'status',
      key: 'status',
      sorter: (a, b) => a.status - b.status,
      render: (status) =>
        status === 1 ? (
          <span className="status-success">Enable</span>
        ) : (
          <span className="status-fail">Disable</span>
        ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (record) => (
        <div className="btn-group">
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`/staking-detail/${record?.poolContract}`)
              showContract(record)
            }}
          >
            Detail
          </Button>

          <Button
            className="button-edit"
            style={canEditStaking ? { display: 'flex' } : { display: 'none' }}
            onClick={() => showEditModal(record)}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ]

  const dataSource = dataStaking.map((item: DataType) => ({
    key: item.poolId,
    poolId: item.poolId,
    earLogo: item.earLogo,
    poolName: item.poolName,
    poolContract: item.poolContract,
    stackingTime: item.stackingTime,
    stakingTimeSecond: item.stakingTimeSecond,
    status: item.status,
  }))

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 6 },
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 14 },
    },
  }

  const beforeUpload = async (file: FileType) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png'
    if (!isJpgOrPng) {
      form.setFields([{ name: 'earLogo', errors: ['You can only upload JPG/PNG file!'] }])
      return false
    }
    const isLt2M = file.size / 1024 / 1024 < 2
    if (!isLt2M) {
      form.setFields([{ name: 'earLogo', errors: ['Image must smaller than 2MB!'] }])
      return false
    }

    // const isCorrectDimensions = await new Promise((resolve) => {
    //   const img = new Image()
    //   const reader = new FileReader()

    //   reader.onload = (event) => {
    //     if (event.target && event.target.result) {
    //       img.src = event.target.result as string
    //     }
    //     img.onload = () => {
    //       const isValidSize = img.width === 116 && img.height === 116
    //       resolve(isValidSize)
    //     }
    //   }

    //   reader.readAsDataURL(file)
    // })

    // if (!isCorrectDimensions) {
    //   form.setFields([
    //     { name: "earLogo", errors: ["Image must be 116x116 pixels!"] },
    //   ])
    //   return false
    // }
    return true
  }

  const uploadLogo: UploadProps['onChange'] = async (info) => {
    // if (info.file.status === "uploading") {
    setLoading(true)

    //   return
    // }
    // if (info.file.status === "done") {

    try {
      // if (!form.getFieldError("earLogo").length) {
      const formData = new FormData()
      formData.append('file', info.file.originFileObj as FileType)
      const response = await AppAdmin.stakingGetLogo(formData)
      if (response.status === 200) {
        const data = await response.data.data
        form.setFields([{ name: 'earLogo', value: data.filePath, errors: [] }])
      }
      // }
    } catch (error) {
      logError('Upload failed')
    } finally {
      setLoading(false)
    }
    // }
  }

  const handleOk = () => {
    form
      .validateFields()
      .then(async (values) => {
        if (modalMode === 'update') {
          const { key: _, stakingTimeSecond: __, ...dataEdit } = values

          const response = await AppAdmin.stakingUpdate(dataEdit)
          setIsModalOpen(false)
          messageApi.open({
            type: 'success',
            content: response.data.message,
          })
          await getDataStaking({ ...pagination, ...filter })
        } else {
          const { key: _, stakingTimeSecond: __, poolId: ___, ...createData } = values
          const response = await AppAdmin.stakingCreate(createData)
          setIsModalOpen(false)
          messageApi.open({
            type: 'success',
            content: response.data.message,
          })
          await getDataStaking({ ...pagination, ...filter })
        }
      })
      .catch((error) => {
        messageApi.open({
          type: 'error',
          content: error.message ? error.message : error.errorFields[0].errors[0],
        })

        // Error message format = "[errorField] [reason]"
        if (error && error.message) {
          form.setFields([
            {
              name: error.message.split(' ')[0],
              errors: [error.message],
            },
          ])
        }
      })
  }

  const viewInfo = async (poolContract: string) => {
    if (dataViewInfo) {
      setDataViewInfo(null)
      return
    }

    if (form.getFieldError('poolContract').length) {
      return
    }
    try {
      const sendPoolContract = { poolContract: poolContract }
      const response = await AppAdmin.getStakingViewInfo(sendPoolContract)
      const dataView = response.data.data

      if (response.data.success === true) {
        setDataViewInfo(dataView)
      }
    } catch (error: any) {
      messageApi.open({
        type: 'error',
        content: error.message ? error.message : error.errorFields[0].errors[0],
      })

      // Error message format = "[errorField] [reason]"
      if (error && error.message) {
        form.setFields([
          {
            name: error.message.split(' ')[0],
            errors: [error.message],
          },
        ])
      }
    }
  }

  const statusOptions = [
    {
      value: 0,
      label: 'Disable',
    },
    {
      value: 1,
      label: 'Enable',
    },
  ]
  const additionalItems = [BREADCRUMBS.STAKING]
  const [data, setData] = useState<any>({})
  const [loadingData, setLoadingData] = useState(false)
  const getData = async () => {
    try {
      const res = await AppAdmin.getDashboard()
      if (res?.data.success) {
        setData(res?.data.data)
      }
    } catch (err) {
      logError(err)
    }
  }

  const getDataFirstTime = useCallback(async () => {
    setLoadingData(true)
    try {
      await getData()
    } catch (err) {
      logError(err)
    } finally {
      setLoadingData(false)
    }
  }, [])

  useEffect(() => {
    getDataFirstTime()
  }, [getDataFirstTime])

  return (
    <>
      {contextHolder}
      <div className="main-section staking">
        <Breadcrumb additionalItems={additionalItems} />
        <div className="dashboard-container">
          <div className="wrap-dashboard">
            <div className="wrap-box">
              <div className="title">Staked Overview</div>
              <div className="wrap-columns">
                <div className="columns">
                  <div className="colum w-3 w-sm-6">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Users</div>
                          <div className="number">
                            {loadingData ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.staked?.totalUser)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <UserOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Staked (MEY)</div>
                          <div className="number">
                            {loadingData ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.staked?.totalStaked)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <DollarOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Pool Cap (MEY)</div>
                          <div className="number">
                            {loadingData ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.staked?.poolCap)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <DollarOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Remaining Pool (MEY)</div>
                          <div className="number">
                            {loadingData ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.staked?.remainingPool)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <DollarOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="main-title">
          Staking admin
          <span className="new-button">
            <Button
              type="primary"
              style={canEditStaking ? { display: 'flex' } : { display: 'none' }}
              onClick={handleClickCreate}
            >
              New Pool
            </Button>
          </span>
        </div>

        <div className="table-row">
          <div className="main-table-row">
            <div>
              <Form
                form={filterForm}
                layout="inline"
                onFinish={(values) => {
                  setFilter(values)
                  setPagination({ ...pagination, pageIndex: 0 })
                }}
                style={{ marginTop: 10, marginBottom: 20 }}
              >
                <Form.Item label="Contract Address" name="poolContract">
                  <Input
                    allowClear
                    placeholder="Enter Contract Address"
                    style={{ width: '100%' }}
                    value={poolContract}
                    onChange={(e) => {
                      setPoolContract(e.target.value)
                    }}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit">
                    Search
                  </Button>
                </Form.Item>
              </Form>
            </div>
            <Table<DataType>
              columns={columns}
              dataSource={dataSource}
              className="main-table"
              scroll={{ x: 1000 }}
              pagination={
                total <= 10
                  ? false
                  : {
                      current: pagination.pageIndex + 1,
                      pageSize: pagination.pageSize,
                      pageSizeOptions: DEFAULT_PAGE_OPTIONS,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                      onShowSizeChange(current, pageSize) {
                        setPagination({
                          pageIndex: current - 1,
                          pageSize,
                        })
                      },
                      onChange(page, pageSize) {
                        setPagination({
                          pageIndex: page - 1,
                          pageSize,
                        })
                      },
                    }
              }
            />

            <Modal
              title={modalMode === 'update' ? 'Update Staking Pool' : 'Create Staking Pool'}
              open={isModalOpen}
              onOk={handleOk}
              onCancel={handleCancel}
              className="modalStaking"
              destroyOnClose={true}
              width={750}
              centered
              okText={modalMode === 'update' ? 'Save' : 'Create'}
            >
              <Form form={form} {...formItemLayout} style={{ maxWidth: 750 }}>
                {modalMode === 'update' ? (
                  <Form.Item
                    label={
                      <span>
                        Pool ID
                        <Popover
                          content={
                            <>
                              <p>
                                The ID of the pool is used to identify the pool in the database.
                              </p>
                              <p>Pool Id cannot be changed</p>
                            </>
                          }
                          placement="top"
                          title="Explain"
                        >
                          <span style={{ marginLeft: 5 }}>
                            <QuestionCircleOutlined />
                          </span>
                        </Popover>
                      </span>
                    }
                    name="poolId"
                  >
                    <Input disabled />
                  </Form.Item>
                ) : null}

                <Form.Item
                  label={
                    <span>
                      Pool Name
                      <Popover
                        content={
                          <>
                            <p>The name of the pool is displayed on the web.</p>
                            <p>
                              Users are not allowed to enter special characters and must not exceed
                              30 characters
                            </p>
                          </>
                        }
                        placement="top"
                        title="Explain"
                      >
                        <span style={{ marginLeft: 5 }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Popover>
                    </span>
                  }
                  name="poolName"
                  rules={[
                    { required: true, message: 'Please input Pool Name!' },
                    {
                      validator(_, value) {
                        if (!value) {
                          return Promise.reject()
                        }

                        if (value.length > 30) {
                          return Promise.reject(new Error('Input must not exceed 30 characters.'))
                        }

                        if (/^[a-zA-Z0-9\s-]+$/.test(value)) {
                          return Promise.resolve()
                        } else {
                          return Promise.reject(
                            new Error('Pool Name cannot contain special characters!')
                          )
                        }
                      },
                    },
                  ]}
                >
                  <Input placeholder="Enter Pool Name" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                  label={
                    <span>
                      Pool Contract Address
                      <Popover
                        content={
                          <p>
                            The contract address of the pool. This is used to get the pool
                            information.
                          </p>
                        }
                        placement="top"
                        title="Explain"
                      >
                        <span style={{ marginLeft: 5 }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Popover>
                    </span>
                  }
                  style={{ marginBottom: 0 }}
                >
                  <div className="pool-contract">
                    <Form.Item
                      name="poolContract"
                      rules={[
                        {
                          required: true,
                          message: 'Please input Pool Contract!',
                        },
                        {
                          validator(_, value) {
                            if (!value) {
                              return Promise.resolve()
                            }
                            if (isValidETHAddress(value) === false) {
                              return Promise.reject('Invalid Pool Contract')
                            }
                            return Promise.resolve()
                          },
                        },
                      ]}
                      style={{ width: '78%' }}
                    >
                      <Input placeholder="Enter Pool Contract" />
                    </Form.Item>

                    <Button
                      type="primary"
                      className="btn-edit"
                      style={{ width: '20%', fontSize: 12, height: 30 }}
                      onClick={() => viewInfo(form.getFieldValue('poolContract'))}
                    >
                      {dataViewInfo ? 'Hide Info' : 'Show Info'}
                    </Button>
                  </div>

                  {dataViewInfo && (
                    <div
                      style={{
                        marginBottom: 20,
                        marginTop: -8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                      }}
                    >
                      <div>
                        <span>Token Stake Address: </span> &nbsp; &nbsp;
                        <Text ellipsis style={{ height: 28 }}>
                          <span>{`${dataViewInfo.tokenStakeAddress.slice(
                            0,
                            5
                          )}...${dataViewInfo.tokenStakeAddress.slice(-5)}`}</span>
                          <Tooltip title="Copy">
                            <Button
                              type="link"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                copy(dataViewInfo.tokenStakeAddress)
                                message.success('Copied Success!')
                              }}
                              style={{ marginLeft: '8px' }}
                            />
                          </Tooltip>
                        </Text>
                      </div>

                      <div>
                        <span>Symbol: </span> &nbsp; &nbsp;
                        {dataViewInfo.tokenStakeSymbol}
                      </div>

                      <div>
                        <span>APY: </span> &nbsp; &nbsp;
                        <Tag color="green">{dataViewInfo.apy}</Tag>
                      </div>

                      <div>
                        <span>Pool Cap: </span> &nbsp; &nbsp;
                        {dataViewInfo.poolCap}
                      </div>

                      <div>
                        <span>Withdraw Fee: </span> &nbsp; &nbsp;
                        <Tag color="green">{dataViewInfo.withdrawFee}</Tag>
                      </div>

                      <div>
                        <span>Pool End Date: </span> &nbsp; &nbsp;
                        {dataViewInfo.poolEndDate}
                      </div>

                      <div>
                        <span>Pool duration: </span> &nbsp; &nbsp;
                        {dataViewInfo.poolDuration}
                      </div>
                    </div>
                  )}
                </Form.Item>

                <Form.Item
                  label={
                    <span>
                      Pool Status
                      <Popover
                        content={
                          <div>
                            <p>Enable: The Pool is active and shows on the web.</p>
                            <p>Disable: The Pool is not active and does not show on web.</p>
                          </div>
                        }
                        placement="top"
                        title="Explain"
                      >
                        <span style={{ marginLeft: 5 }}>
                          <QuestionCircleOutlined />
                        </span>
                      </Popover>
                    </span>
                  }
                  name="status"
                  rules={[{ required: true, message: 'Please input Status!' }]}
                >
                  <Select placeholder="Choose Status" options={statusOptions} />
                </Form.Item>

                <Form.Item
                  label={
                    <span style={{ cursor: 'default' }} onClick={(e) => e.preventDefault()}>
                      Logo
                    </span>
                  }
                  name="earLogo"
                >
                  <Upload
                    name="file"
                    listType="picture-circle"
                    showUploadList={false}
                    customRequest={() => {}} // To prevent default upload behavior
                    beforeUpload={beforeUpload} // TODO: Validate file before upload
                    onChange={uploadLogo}
                    multiple={false}
                  >
                    <UploadButton value={form.getFieldValue('earLogo')} loading={loading} />
                  </Upload>
                </Form.Item>

                <Form.Item label="" style={{ marginLeft: 80, width: '100%' }}>
                  <Text italic>You can only upload JPG/PNG file.</Text> <br />
                  <Text italic>The image should have the size of 116x116 px.</Text> <br />
                  <Text italic>The image must smaller than 2MB.</Text>
                </Form.Item>
              </Form>
            </Modal>
          </div>
        </div>
      </div>
    </>
  )
}
export default Staking
